import mapboxgl from 'mapbox-gl'
import { useCallback, useEffect, useRef } from 'react'
import { Dispatch } from 'redux'
import {
  handleClick,
  handleHover,
  handleMoveEnd,
  handleZoomEnd,
  resetHoverState,
} from '../../../components/MapSection/features/publicWaterSystem'
import { mapColorPalette, stateToRegionByName } from '../../../constants/Map'
import usStates from '../../../constants/Map/polygons/us-states.json'
import { useError } from '../../../context/ErrorContext'
import {
  PublicWaterSystemsAggregatesActions,
  ZoomLevel,
} from '../../../store/publicWaterSystem'
import { WaterSystemType } from '../../../store/waterSystems'

interface MapInitializationProps {
  regionsViolationsRef: React.MutableRefObject<any>
  prevSelectedCode: React.MutableRefObject<string | null>
  prevSelectedCounty: React.MutableRefObject<string | null>
  countyViolationsRef: React.MutableRefObject<any>
  markersRef: React.MutableRefObject<any[]>
  dispatch: Dispatch
  waterSystemType: WaterSystemType
  waterSystemMapMetricsRef: React.MutableRefObject<any>
  statesRef: React.MutableRefObject<any>
  selectedStateRef: React.MutableRefObject<any>
  selectedAddressRef: React.MutableRefObject<any>
}

export const useMapInitialization = ({ waterSystemType, ...props }: MapInitializationProps) => {
  const { setError } = useError()


  const mapRef = useRef<mapboxgl.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const hasDispatchedZoomOut = useRef<boolean>(false)

  const mapBoxAccessToken =
    (window.__ENV__ &&
      window.__ENV__?.MAPBOX_ACCESS_KEY !== '__MAPBOX_ACCESS_KEY__' &&
      window.__ENV__?.MAPBOX_ACCESS_KEY) ||
    process.env.REACT_APP_MAPBOX_ACCESS_KEY ||
    ''

  const initializeMapSources = useCallback(() => {
    if (!mapRef.current) return

    mapRef.current.addSource('maine', {
      type: 'geojson',
      data: usStates as any,
    })
  }, [])
  const initializeMapLayers = useCallback((regionToColorMapping: Record<string, string>) => {
    if (!mapRef.current) return
    mapRef.current.addLayer({
      id: 'maine',
      type: 'fill',
      source: 'maine',
      layout: {},
      paint: {
        'fill-color': [
          'match',
          ['get', 'name'],
          ...Object.entries(stateToRegionByName)
            .map(([stateName, { epaRegion }]) => {
              const color = regionToColorMapping[epaRegion] || '#ffffff'
              return [stateName, color]
            })
            .flat(),
          '#ffffff',
        ],
        'fill-opacity': 0.5,
      },
    })

    mapRef.current.addLayer({
      id: 'outline',
      type: 'line',
      source: 'maine',
      layout: {},
      paint: {
        'line-color': '#000',
        'line-width': 1,
      },
    })
  }, [])
  const initializeMapEvents = useCallback(() => {
    if (!mapRef.current) return

    let prevZoom = mapRef.current.getZoom()

    let currentPopup: mapboxgl.Popup | null = null

    mapRef.current.on('zoomend', ()=>handleZoomEnd({
      mapRef,
      prevZoom,
      prevSelectedCode: props.prevSelectedCode,
      regionsViolationsRef: props.regionsViolationsRef,
      countyViolationsRef: props.countyViolationsRef,
    }))


    mapRef.current.on('mousemove', 'maine', (e) => {
      mapRef.current!.getCanvas().style.cursor = 'pointer'

      if (currentPopup) {
        currentPopup.remove()
      }

      currentPopup = handleHover(
        e,
        mapRef,
        props.regionsViolationsRef,
        props.countyViolationsRef,
        props.waterSystemMapMetricsRef,
        props.dispatch,
      )
    })

    mapRef.current.on('mouseleave', 'maine', () => {
      mapRef.current!.getCanvas().style.cursor = ''
      if (currentPopup) {
        currentPopup.remove()
        currentPopup = null
      }
      resetHoverState()
    })

    mapRef.current.on('moveend', () =>
      handleMoveEnd({
        mapRef,
        prevSelectedCounty: props.prevSelectedCounty,
        prevSelectedCode: props.prevSelectedCode,
        markersRef: props.markersRef,
        dispatch: props.dispatch,
        countyViolationsRef: props.countyViolationsRef,
        regionsViolationsRef: props.regionsViolationsRef,
        selectedAddressRef: props.selectedAddressRef,
        hasDispatchedZoomOut: hasDispatchedZoomOut,
      }),
    )

    mapRef.current.on('click', 'maine', (e) =>
      handleClick(e, mapRef, props.statesRef, props.dispatch),
    )
  }, [
    props.dispatch,
    props.prevSelectedCode,
    props.statesRef,
    props.selectedStateRef,
    props.waterSystemMapMetricsRef,
    props.markersRef,
  ])
  const initializeMap = useCallback(() => {
    if (!mapRef.current) return

    const sortedRegions = Array.isArray(props.regionsViolationsRef.current)
      ? [...props.regionsViolationsRef.current].sort((a, b) => b.violations - a.violations)
      : []

    const regionToColorMapping = sortedRegions.reduce(
      (acc, region, index) => {
        acc[region?.epaRegion] = mapColorPalette[index]
        return acc
      },
      {} as Record<string, string>,
    )

    initializeMapSources()
    initializeMapLayers(regionToColorMapping)
    initializeMapEvents()
  }, [initializeMapSources, initializeMapLayers, initializeMapEvents])

  useEffect(() => {
    if (!mapBoxAccessToken) {
      setError(new Error('Missing Mapbox API token in environment variables'))
      return
    }

    // Reset zoom level to REGION and clean up markers on initial load
    props.dispatch(PublicWaterSystemsAggregatesActions.setZoomLevel(ZoomLevel.REGION))

    mapboxgl.accessToken = mapBoxAccessToken

    // Cleanup previous map instance
    if (mapRef.current) {
      mapRef.current.remove()
      mapRef.current = null
    }

    // Initialize new map
    if (mapContainerRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-98.5795, 39.8283],
        zoom: 4,
      })

      mapRef.current.once('load', initializeMap)
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }

      //props.dispatch(AddressActions.setSelectedAddress(null))
      //removeMarkers(props.markersRef);
    }

  }, [])

  return { mapRef, mapContainerRef }
}
